import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { DataViews } from "@wordpress/dataviews";
import { format } from "date-fns";

import { adminProductsQueryOptions } from "../../../../data/queries/products";
import { getProductType } from "../../../../product";
import { getShippingPrice } from "../../shipping/wilayas";
import AdminPage from "../admin-page";
import style from "./style.module.scss";
import type { Product } from "../../../../data/api/products";

interface EnrichedProduct extends Product {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  totalPrice: number;
  formattedDate: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DataViewsView = any;

function ProductList() {
  const navigate = useNavigate();

  // DataViews state
  const [view, setView] = useState<DataViewsView>({
    type: "table",
    perPage: 20,
    page: 1,
    titleField: "uid",
    fields: [
      "status",
      "time",
      "name",
      "customerName",
      "customerEmail",
      "customerPhone",
      "totalPrice",
    ],
    filters: [
      { field: "status", operator: "is", value: "order" }, // Default filter
    ],
    sort: {
      field: "time",
      direction: "desc",
    },
    search: "",
    hiddenFields: [],
    layout: {},
  });

  // Extract filters for query
  const statusFilter = view.filters?.find(
    (f: { field: string; value: string }) => f.field === "status",
  )?.value as "draft" | "order" | "shipped" | "archived" | undefined;
  const nameFilter = view.filters?.find(
    (f: { field: string; value: string }) => f.field === "name",
  )?.value;

  // Fetch data
  const { data: products = [], isLoading } = useQuery(
    adminProductsQueryOptions({
      status: statusFilter,
      name: nameFilter,
    }),
  );

  // Enrich products with calculated fields
  const enrichedData = useMemo(
    () =>
      products.map((product): EnrichedProduct => {
        const productType = getProductType(product.name);
        const wilaya = product?.shipping?.wilaya;
        const shippingPrice = wilaya ? getShippingPrice(wilaya) : 0;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const totalPrice =
          productType?.price(product.options as any, product.promoCode, shippingPrice) ?? 0;

        return {
          ...product,
          customerName: product?.shipping?.name || "-",
          customerEmail: product?.shipping?.email || "-",
          customerPhone: product?.shipping?.phone || "-",
          totalPrice,
          formattedDate: product.time ? format(new Date(product.time), "MM/dd/yyyy HH:mm") : "-",
        };
      }),
    [products],
  );

  // Define columns
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fields: any[] = [
    {
      id: "uid",
      header: "Product UID",
      enableHiding: false,
      enableSorting: false,
      render: ({ item }: { item: EnrichedProduct }) => (
        <button onClick={() => navigate(`/admin/${item.uid}`)} className={style.uidLink}>
          {item.uid}
        </button>
      ),
    },
    {
      id: "status",
      header: "Status",
      enableHiding: false,
      enableSorting: true,
      type: "enumeration",
      elements: [
        { value: "draft", label: "Draft" },
        { value: "order", label: "Order" },
        { value: "shipped", label: "Shipped" },
        { value: "archived", label: "Archived" },
      ],
      filterBy: {
        isPrimary: true,
        operators: ["is", "isNot"],
      },
    },
    {
      id: "time",
      header: "Order Date",
      render: ({ item }: { item: EnrichedProduct }) => item.formattedDate,
      enableSorting: true,
    },
    {
      id: "name",
      header: "Product Type",
      type: "enumeration",
      elements: [
        { value: "photo-book", label: "Photo Book" },
        { value: "calendar", label: "Calendar" },
      ],
      enableSorting: false,
    },
    {
      id: "customerName",
      header: "Customer Name",
      enableSorting: false,
    },
    {
      id: "customerEmail",
      header: "Customer Email",
      enableSorting: false,
    },
    {
      id: "customerPhone",
      header: "Customer Phone",
      enableSorting: false,
    },
    {
      id: "totalPrice",
      header: "Total Price",
      render: ({ item }: { item: EnrichedProduct }) => `${item.totalPrice} DA`,
      enableSorting: false,
    },
  ];

  // Pagination info
  const paginationInfo = {
    totalItems: enrichedData.length,
    totalPages: Math.ceil(enrichedData.length / view.perPage),
  };

  return (
    <AdminPage title="Products">
      <DataViews
        data={enrichedData}
        fields={fields}
        view={view}
        onChangeView={setView}
        actions={[]}
        paginationInfo={paginationInfo}
        isLoading={isLoading}
        defaultLayouts={{ table: {} }}
        getItemId={(item: EnrichedProduct) => item.uid}
      />
    </AdminPage>
  );
}

export default ProductList;
